import React, { useState, useEffect } from 'react';
import get from 'lodash.get';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, Configuration, TicketDeliverySettings } from 'services/configuration';
import { actions as ticketingActions, selectors as ticketingSelectors, DeliveryMethod } from 'services/ticketing';
import { DeliveryScreen } from './types';
import { DeliverySelection } from './components/DeliverySelection';
import { DeliveryEmailCollection } from './components/DeliveryEmailCollection';
import { DeliveryPhoneCollection } from './components/DeliveryPhoneCollection';

const DeliverySelectionView = () => {
  const { savePreferredDeliveryMethod, saveDeliveryEmail, saveDeliveryPhoneNumber } = useActions({
    savePreferredDeliveryMethod: ticketingActions.savePreferredDeliveryMethod,
    saveDeliveryEmail: ticketingActions.saveDeliveryEmail,
    saveDeliveryPhoneNumber: ticketingActions.saveDeliveryPhoneNumber
  });
  const location = useLocation();
  const navigate = useNavigate();
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const ticketDeliverySettings: TicketDeliverySettings = get(config, `${ticketingPath}.ticketDeliverySettings`, {});
  const emailDeliveryEnabled = get(ticketDeliverySettings, 'email.enabled', false);
  const textDeliveryEnabled = get(ticketDeliverySettings, 'textMessage.enabled', false);
  const printDeliveryEnabled = get(ticketDeliverySettings, 'print.enabled', false);

  const [screen, setScreen] = useState<DeliveryScreen | null>(null);
  const [deliveryMethodsCount, setDeliveryMethodsCount] = useState<number | null>(null);

  // On mount: determine the number of delivery options
  useEffect(() => {
    let count = 0;
    if (emailDeliveryEnabled) count++;
    if (textDeliveryEnabled) count++;
    if (printDeliveryEnabled) count++;
    setDeliveryMethodsCount(count);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show delivery selection, or auto select if only one option
  useEffect(() => {
    if (deliveryMethodsCount === 0) {
      goToNextPage('print', '');
    } else if (deliveryMethodsCount === 1) {
      if (emailDeliveryEnabled) {
        goToNextPage('email', '');
      } else if (textDeliveryEnabled) {
        goToNextPage('textMessage', '');
      } else {
        goToNextPage('print', '');
      }
    } else if (deliveryMethodsCount && deliveryMethodsCount > 1) {
      setScreen('delivery-selection');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMethodsCount]);

  const goToPrevPage = () => {
    if (
      (screen === 'email-collection' || screen === 'phone-number-collection') &&
      deliveryMethodsCount &&
      deliveryMethodsCount > 1
    ) {
      setScreen('delivery-selection');
    } else {
      navigate('/ticketing/cart/survey', { state: { from: location.pathname } });
    }
  };

  const goToNextPage = (selectedOption: DeliveryMethod, value: string) => {
    if (selectedOption === 'email' && !value) {
      setScreen('email-collection');
    } else if (selectedOption === 'textMessage' && !value) {
      setScreen('phone-number-collection');
    } else {
      saveDeliveryAddressValue(selectedOption, value);
      savePreferredDeliveryMethod(selectedOption.toString());
      navigate('/ticketing/purchase/payment');
    }
  };

  const saveDeliveryAddressValue = (selectedOption: DeliveryMethod, value: string) => {
    if (selectedOption === 'email') {
      saveDeliveryEmail(value);
    } else if (selectedOption === 'textMessage') {
      saveDeliveryPhoneNumber(value);
    }
  };

  const getScreen = (deliveryScreen: DeliveryScreen | null) => {
    switch (deliveryScreen) {
      case 'email-collection':
        return <DeliveryEmailCollection onNext={goToNextPage} onPrev={goToPrevPage} />;
      case 'phone-number-collection':
        return <DeliveryPhoneCollection onNext={goToNextPage} onPrev={goToPrevPage} />;
      case 'delivery-selection':
        return <DeliverySelection onNext={goToNextPage} onPrev={goToPrevPage} />;
      default:
        return null;
    }
  };

  return <>{getScreen(screen)}</>;
};

export default DeliverySelectionView;
